import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Check, Clock, Rocket, Phone, Database, Users, MessageSquare, Star } from "lucide-react";

const DAYS = [
  {
    day: "Day 1",
    title: "Contract Signs — Kickoff",
    Icon: Check,
    accent: "bg-emerald-600",
    tasks: [
      "Collect signed subscription agreement + ACH authorization",
      "Charge $895 onboarding fee via Stripe",
      "Create the dealership's admin account in Decline Drive",
      "Schedule the 45-minute kickoff call for Day 2",
      "Send welcome email with login link + One-Pager PDF attachment",
    ],
  },
  {
    day: "Day 2",
    title: "Kickoff Call (45 min)",
    Icon: Phone,
    accent: "bg-navy",
    tasks: [
      "Configure Settings: dealership name, phone, address, coupon prefix",
      "Set discount %s (default 30/40 — adjust to their preference)",
      "Add all service advisors with names + cell phones (for pre-flight texts)",
      "Walk them through Dashboard, Segments, SMS Log, Statement",
      "Confirm data pipeline — how they'll get us the nightly decline export",
      "Book a follow-up for Day 4 (data import review)",
    ],
  },
  {
    day: "Day 3-4",
    title: "Data Pipeline & First Import",
    Icon: Database,
    accent: "bg-sky-600",
    tasks: [
      "Import first CSV of declined ROs (last 30 days minimum)",
      "Verify all customer phone numbers land correctly",
      "Verify advisor names match their DMS export column",
      "Send a test SMS to the dealer principal's cell using their own template",
      "Review any parse errors together — clean import = clean pitch results",
    ],
  },
  {
    day: "Day 5",
    title: "Advisor Training",
    Icon: Users,
    accent: "bg-amber-600",
    tasks: [
      "Ship printed Academy manuals to the dealership (1 per advisor + 1 per manager)",
      "30-min video call with all advisors (Zoom or Teams)",
      "Cover Ch. 21 (Follow-Up is Where Professionals Separate Themselves)",
      "Cover Ch. 45 (The Appointment Starts Before the Customer Arrives)",
      "Show them what the pre-flight text looks like on their own phones",
    ],
  },
  {
    day: "Day 6",
    title: "Soft Launch (10 test customers)",
    Icon: MessageSquare,
    accent: "bg-purple-600",
    tasks: [
      "Pick 10 recent decline customers with the dealer's blessing",
      "Turn on the offer campaign against just that batch",
      "Watch the SMS log live with the dealer principal",
      "Field the first customer response together — pattern the advisor",
      "Fix any messaging tweaks before full rollout",
    ],
  },
  {
    day: "Day 7",
    title: "Full Go-Live",
    Icon: Rocket,
    accent: "bg-orange-600",
    tasks: [
      "Enable nightly cron for their tenant",
      "First advisor pre-flight text fires on the first customer booking",
      "First recovered RO hits the KPI dashboard within 48 hours",
      "Schedule the weekly check-in call for Weeks 1-4",
      "Celebrate the first booked appointment — take a screenshot for the case study",
    ],
  },
  {
    day: "Weeks 2-4",
    title: "Optimize & Retain",
    Icon: Star,
    accent: "bg-rose-600",
    tasks: [
      "Weekly 15-min check-in call for first 4 weeks",
      "Review advisor scorecards — praise the top recovery advisor by name",
      "Review CSI text open rate and any negative replies",
      "Introduce them to Text Blasts: Free Oil, Parts Arrived, Fast Services",
      "Ask for a written testimonial once they hit $5k recovered — use in future pitches",
    ],
  },
];

const NORTH_STARS = [
  "One recovered RO within 48 hours of go-live",
  "$5,000 recovered service revenue in the first 30 days",
  "First advisor comes to you and says \"this thing actually works\"",
  "First dealer principal refers you to another rooftop principal",
];

export default function OnboardingPage() {
  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Dealer Onboarding Playbook" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Post-Sign Playbook</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Dealer Onboarding Checklist
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            The exact 7-day playbook to run every dealership through after they sign. Zero surprises,
            zero &quot;did we forget something?&quot; moments. Every dealer gets the same premium onboarding.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-onboarding-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      {/* North Stars */}
      <Card className="p-5 mb-6 border-[hsl(var(--gold))]/40 !bg-navy text-white" data-testid="onboarding-north-stars">
        <div className="font-mono-label text-[hsl(var(--gold-light))] text-xs">
          The Four North Stars
        </div>
        <div className="font-display font-bold text-lg mt-1 text-white">
          What every onboarding is aiming for
        </div>
        <ul className="mt-3 space-y-1.5 text-sm text-white/90">
          {NORTH_STARS.map((n, i) => (
            <li key={i} className="flex items-start gap-2">
              <Star className="w-4 h-4 mt-0.5 text-[hsl(var(--gold))] shrink-0" strokeWidth={2} />
              <span className="text-white">{n}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Day-by-day cards */}
      <div className="space-y-4">
        {DAYS.map(({ day, title, Icon, accent, tasks }, i) => (
          <Card key={day} className="p-0 overflow-hidden border-border" data-testid={`onboarding-${day.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-stretch">
              <div className={`w-24 shrink-0 ${accent} text-white grid place-items-center p-4`}>
                <div className="text-center">
                  <Icon className="w-6 h-6 mx-auto" strokeWidth={2} />
                  <div className="font-mono-label text-white/80 text-[0.55rem] mt-1.5">
                    {day}
                  </div>
                </div>
              </div>
              <div className="flex-1 p-5">
                <div className="font-display font-bold text-lg tracking-tight">{title}</div>
                <ul className="mt-2 space-y-1.5">
                  {tasks.map((t, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" strokeWidth={2.5} />
                      <span className="text-neutral-700 dark:text-neutral-300">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground max-w-3xl">
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <b>Estimated your time investment per dealer:</b> ~6 hours over 7 days. After Week 4, drops to
            ~30 min/month per dealer for the check-in call. At 20 dealers = 10 hrs/month total. That's the
            hands-off scale you asked about.
          </span>
        </div>
      </div>
    </DashboardShell>
  );
}
